/**
 * CommentMessageContent.jsx
 *
 * Renders a single comment message's content.
 *
 *  - contentType === "prosemirror"  →  read-only ProseMirror DOMSerializer output
 *  - contentType === "text" or absent  →  plain <Typography>
 *
 * This keeps ListOfCommentsYjs clean and ensures full backward compatibility
 * with old plain-text messages.
 */

import React, {useEffect, useRef} from 'react';
import {Typography} from '@mui/material';
import {DOMSerializer} from 'prosemirror-model';
import {commentSchema} from './commentSchema';
import './commentEditor.css';

// Serialiser is cheap to create and schema-scoped — cache it module-level
const serializer = DOMSerializer.fromSchema(commentSchema);

/**
 * Renders a single ProseMirror doc JSON as read-only HTML using DOMSerializer.
 */
function RichTextContent({json}) {
    const containerRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) return;
        // Clear previous content
        containerRef.current.innerHTML = '';
        try {
            const doc = commentSchema.nodeFromJSON(json);
            const fragment = serializer.serializeFragment(doc.content);
            containerRef.current.appendChild(fragment);
        } catch (e) {
            console.error('[CommentMessageContent] Failed to render rich-text message:', e);
            containerRef.current.textContent = '[Could not render message]';
        }
    }, [json]);

    return (
        <div
            ref={containerRef}
            className="comment-message-content"
            aria-readonly="true"
        />
    );
}

/**
 * CommentMessageContent
 *
 * Props:
 *  - content       {string|object}  raw message content
 *  - contentType   {"text"|"prosemirror"|undefined}
 */
export default function CommentMessageContent({content, contentType}) {
    if (contentType === 'prosemirror' && content && typeof content === 'object') {
        return <RichTextContent json={content}/>;
    }

    // Plain-text fallback (old messages, or contentType === "text")
    return (
        <Typography variant="body1" color="text.primary" sx={{fontSize: '0.875rem', wordBreak: 'break-word'}}>
            {typeof content === 'string' ? content : '[Invalid message]'}
        </Typography>
    );
}

